// src/stores/useStreakStore.ts
// Zustand store wrapping the streak service.
//
// Lifecycle:
//   1. App calls `attachAuth(uid)` whenever the auth state changes.
//   2. The store loads the record (IDB → Firestore reconcile) and exposes
//      `displayStreak` for UI consumption.
//   3. UI components either read `displayStreak` directly or wrap with the
//      `useStreakTick()` hook which forces a re-render every 60s so that
//      day rollovers update without a manual refresh.
//   4. `recordActivity()` is called on every test or exercise section
//      completion. Idempotent per-day.

import { create } from 'zustand';
import {
  bumpStreak,
  computeDisplayStreak,
  loadStreak,
  STREAK_CAP,
  type StreakRecord,
} from '../services/streakService';
import { useAuthStore } from './useAuthStore';

interface StreakState {
  uid: string | null;
  record: StreakRecord | null;
  isLoading: boolean;

  /** Re-attach when the auth user changes. Pass null on logout. */
  attachAuth: (uid: string | null) => Promise<void>;

  /** Call after any qualifying completion. Safe to over-call (idempotent per-day). */
  recordActivity: () => Promise<void>;

  /** Force a re-read from IDB/Firestore. Rarely needed. */
  refresh: () => Promise<void>;

  /** Display value derived from the stored record + current local date. */
  displayStreak: () => number;
}

export const useStreakStore = create<StreakState>((set, get) => ({
  uid: null,
  record: null,
  isLoading: false,

  attachAuth: async (uid) => {
    // No-op if same uid and we already have a record.
    if (uid === get().uid && get().record) return;
    set({ uid, isLoading: true });
    try {
      const record = await loadStreak(uid);
      // Bail if uid changed mid-flight (e.g. logout during load).
      if (get().uid !== uid) return;
      set({ record, isLoading: false });
    } catch (err) {
      console.warn('[useStreakStore] load failed:', err);
      set({ isLoading: false });
    }
  },

  recordActivity: async () => {
    const { uid } = get();
    // Streak is a premium-only feature — non-subscribers stay at 0.
    const profile = useAuthStore.getState().profile;
    const isSubscribed =
      profile?.subscription?.status === 'active' ||
      profile?.hasFullAccess === true;
    if (!isSubscribed) return;
    try {
      const next = await bumpStreak(uid);
      // Guard against late-arriving bump after auth changed.
      if (get().uid !== uid) return;
      set({ record: next });
    } catch (err) {
      console.warn('[useStreakStore] bump failed:', err);
    }
  },

  refresh: async () => {
    const { uid } = get();
    try {
      const record = await loadStreak(uid);
      if (get().uid !== uid) return;
      set({ record });
    } catch (err) {
      console.warn('[useStreakStore] refresh failed:', err);
    }
  },

  displayStreak: () => computeDisplayStreak(get().record),
}));

export { STREAK_CAP };